import React, { useEffect, useState } from "react";
import Draggable from "react-draggable";
import {
    Dialog,
    DialogContent,
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    CircularProgress,
    Divider,
    TextField,
    Paper,
    Box,
    Button,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faEdit, faSearch } from "@fortawesome/free-solid-svg-icons";

// Custom Paper Component for Draggable functionality
const DraggablePaper = (props) => {
    return (
        <Draggable
            handle="#draggable-dialog-title" // ID of the element to act as the drag handle
            cancel={'[class*="MuiDialogContent-root"]'} // Prevent dragging inside the content
        >
            <Paper {...props} />
        </Draggable>
    );
};

const TranscriptDialog = ({ open, transcript, loading, onClose, videoId, onSaveTranscript }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [draftTranscript, setDraftTranscript] = useState("");
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        if (open) {
            setDraftTranscript(transcript?.transcript_text || "");
            setSearchTerm("");
            setIsEditing(false); // Start in high-quality view mode
        }
    }, [transcript?.transcript_text, open]);

    const handleSearchKeyDown = (e) => {
        if (e.key === "Enter" && searchTerm && draftTranscript) {
            if (isEditing) {
                const textarea = document.getElementById("transcript-editor-native-textarea");
                if (textarea) {
                    const index = draftTranscript.toLowerCase().indexOf(searchTerm.toLowerCase());
                    if (index !== -1) {
                        textarea.focus();
                        textarea.setSelectionRange(index, index + searchTerm.length);
                        textarea.scrollTop = (draftTranscript.substring(0, index).split("\n").length - 3) * 21;
                    }
                }
            }
        }
    };

    const highlightMatches = (text, query) => {
        if (!query) return text;
        const escapedQuery = query.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
        const regex = new RegExp(`(${escapedQuery})`, "gi");
        const parts = text.split(regex);
        return parts.map((part, index) =>
            part.toLowerCase() === query.toLowerCase() ? (
                <mark key={index} style={{ backgroundColor: "yellow", padding: "1px 2px", borderRadius: "2px", fontWeight: "bold" }}>
                    {part}
                </mark>
            ) : (
                part
            )
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperComponent={DraggablePaper}
            aria-labelledby="draggable-dialog-title"
        >
            <AppBar
                position="static"
                id="draggable-dialog-title"
                style={{ backgroundColor: "#0B194A", color: "white", cursor: "move" }}
            >
                <Toolbar>
                    <Typography variant="h6" style={{ flex: 1 }}>
                        Transcript
                    </Typography>

                    <TextField
                        variant="outlined"
                        placeholder="Search & Press Enter..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <FontAwesomeIcon
                                    icon={faMagnifyingGlass}
                                    style={{ marginRight: "8px", color: "#888" }}
                                />
                            ),
                        }}
                        style={{
                            backgroundColor: "white",
                            borderRadius: "5px",
                            marginRight: "20px",
                            width: "300px",
                        }}
                    />
                    <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
                        <CloseIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            <DialogContent style={{ padding: "20px" }}>
                {loading ? (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
                        <CircularProgress />
                    </div>
                ) : transcript ? (
                    <Box style={{ display: "flex", flexDirection: "column", height: "75vh" }}>
                        <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                            <Box style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                                <Typography variant="h6" style={{ fontWeight: "bold" }}>
                                    {transcript.title}
                                </Typography>
                                <Button 
                                    variant="outlined" 
                                    size="small"
                                    onClick={() => setIsEditing(!isEditing)}
                                    title={isEditing ? "View Highlighting" : "Edit Transcript"}
                                >
                                    <FontAwesomeIcon icon={isEditing ? faSearch : faEdit} />
                                </Button>
                            </Box>
                            <Box style={{ display: "flex", gap: "10px" }}>
                                <Button variant="outlined" size="small" onClick={() => setDraftTranscript("")}>Clear</Button>
                                <Button variant="outlined" size="small" onClick={() => setDraftTranscript(transcript?.transcript_text || "")}>Reset</Button>
                                <Button
                                    variant="contained"
                                    size="small"
                                    style={{ backgroundColor: "#0B194A", color: "white" }}
                                    onClick={async () => {
                                        await onSaveTranscript?.(videoId, draftTranscript);
                                        onClose?.();
                                    }}
                                >
                                    Save Changes
                                </Button>
                            </Box>
                        </Box>

                        <Divider style={{ marginBottom: "15px" }} />

                        {/* SINGLE BOX VIEW TOGGLED BETWEEN SEARCH AND EDIT */}
                        <Box style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                            {isEditing ? (
                                <textarea
                                    id="transcript-editor-native-textarea"
                                    value={draftTranscript}
                                    onChange={(e) => setDraftTranscript(e.target.value)}
                                    placeholder="Edit the transcript here..."
                                    style={{ 
                                        flex: 1, 
                                        padding: "15px",
                                        fontFamily: "'Roboto Mono', monospace",
                                        fontSize: "14px",
                                        lineHeight: "1.6",
                                        border: "1px solid #0B194A",
                                        borderRadius: "6px",
                                        resize: "none",
                                        outline: "none",
                                        overflowY: "auto",
                                        backgroundColor: "#fff"
                                    }}
                                />
                            ) : (
                                <Box 
                                    style={{ 
                                        flex: 1, 
                                        border: "1px solid #ccc", 
                                        borderRadius: "6px", 
                                        padding: "15px", 
                                        backgroundColor: "#f9f9f9",
                                        overflowY: "auto",
                                        whiteSpace: "pre-wrap",
                                        fontSize: "14px",
                                        lineHeight: "1.6",
                                        color: "#222"
                                    }}
                                >
                                    {highlightMatches(draftTranscript, searchTerm)}
                                </Box>
                            )}
                        </Box>
                        
                        <Typography variant="caption" color="textSecondary" style={{ textAlign: "right", marginTop: "10px" }}>
                            <em>{isEditing ? "Switch to 'View Matches' to see yellow highlights." : "Click 'Edit Transcript' to make manual changes."}</em>
                        </Typography>
                    </Box>
                ) : (
                    <Typography variant="body2" color="textSecondary">
                        No transcript available.
                    </Typography>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default TranscriptDialog;
